/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/sale_transactions-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-sale_transactions-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/sale_transactions-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, conditions_of_sale_id, archived_by_user_profile_id, archive_batch_id
 * - Sortable fields: close_date, listed_date, created_at, updated_at
 * - Required fields: id, permission_level_id, is_colliers_sale
 * - Readonly fields: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/sale_transactions-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-sale_transactions-api-client.ts (API client)
 * - client/api/generated_schemas/sale_transactions.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/sale_transactions-entity-config.ts
 * Public fields: 34/34 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SaleTransactionsClient, SaleTransactionsQueryOptions } from '../generated_clients/generated-sale_transactions-api-client.js';
import {
  SaleTransactionsInsert,
  SaleTransactionsUpdate,
  SaleTransactionsFieldConfig
} from '../../types/generated_schemas/generated-sale_transactions-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/sale_transactions_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const saleTransactionsKeys = {
  all: ['sale_transactions'] as const,
  lists: () => [...saleTransactionsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<SaleTransactionsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...saleTransactionsKeys.lists(), { filters, ...page }] as const,
  details: () => [...saleTransactionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...saleTransactionsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...saleTransactionsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...saleTransactionsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...saleTransactionsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...saleTransactionsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useSaleTransactionsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new SaleTransactionsClient();
};

/**
 * Hook to get all sale_transactions with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with sale_transactions data
 */
export const useSaleTransactionsList = (options?: SaleTransactionsQueryOptions) => {
  const client = useSaleTransactionsClient();
  
  return useQuery({
    queryKey: saleTransactionsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single sale_transaction by ID
 *
 * @param id - The sale_transaction ID
 * @returns React Query result with single sale_transaction data
 */
export const useSaleTransactions = (id: string) => {
  const client = useSaleTransactionsClient();
  
  return useQuery({
    queryKey: saleTransactionsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/sale_transactions
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/sale_transactions_business_hooks/
 */

/**
 * Hook to create a new sale_transaction
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, permission_level_id, is_colliers_sale
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for creating sale_transaction
 */
export const useCreateSaleTransactions = () => {
  const client = useSaleTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: SaleTransactionsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: saleTransactionsKeys.all });
    },
  });
};

/**
 * Hook to update an existing sale_transaction
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for updating sale_transaction
 */
export const useUpdateSaleTransactions = () => {
  const client = useSaleTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SaleTransactionsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: saleTransactionsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: saleTransactionsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a sale_transaction
 *
 * @returns React Query mutation for deleting sale_transaction
 */
export const useDeleteSaleTransactions = () => {
  const client = useSaleTransactionsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: saleTransactionsKeys.all });
    },
  });
};

/**
 * Hook to count sale_transactions with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, conditions_of_sale_id, archived_by_user_profile_id, archive_batch_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useSaleTransactionsCount = (filter?: Partial<Record<typeof SaleTransactionsFieldConfig.filterable[number], any>>) => {
  const client = useSaleTransactionsClient();
  
  return useQuery({
    queryKey: saleTransactionsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useSaleTransactionsFieldConfig = () => {
  return SaleTransactionsFieldConfig;
};